import * as React from 'react';
import { cn } from '@ai-content-hub/content-kit';
import { Sparkles, Loader2 } from 'lucide-react';
import { ChatHistory } from './ChatHistory';
import { ChatInput } from './ChatInput';
import type { ChatMessageData, ChatJobStatus } from './types';
import type { PromptFormApiConfig, UploadedDocument } from '../../types';
import { usePromptTranslations } from '../../i18n';

export interface ChatLayoutProps {
    messages: ChatMessageData[];
    onSendMessage: (text: string, documents?: UploadedDocument[]) => void;
    onRollback?: (version: number) => void;
    isRollbackLoading?: boolean;
    loading?: boolean;
    disabled?: boolean;
    apiConfig?: PromptFormApiConfig;
    title?: string;
    className?: string;
    jobStatus?: ChatJobStatus | null;
}

const RollbackStatusBar: React.FC = () => {
    const pt = usePromptTranslations();
    return (
    <div className="chat-rollback-status flex items-center gap-2 px-4 py-2 text-xs border-t border-border/40 bg-primary/5 text-primary">
        <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" />
        <span>{pt.rollingBack}</span>
    </div>
    );
};

export const ChatLayout: React.FC<ChatLayoutProps> = ({
    messages,
    onSendMessage,
    onRollback,
    isRollbackLoading,
    loading = false,
    disabled = false,
    apiConfig,
    title = 'AI Tạo câu hỏi',
    className,
    jobStatus,
}) => (
    <div className={cn('chat-layout flex flex-col h-full', className)}>
        <div className="chat-layout-header flex items-center gap-2 px-4 py-3 border-b border-border/60">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold text-foreground">{title}</span>
        </div>

        <ChatHistory
            messages={messages}
            onRollback={onRollback}
            isRollbackLoading={isRollbackLoading}
            className="flex-1 min-h-0"
        />

        {/*
            EAA-1740: the chat message itself already shows a "Generating..."
            pending bubble (see useGenerationHistory.appendGeneration), so a
            second "Generating (step X/Y)..." bar here was a duplicate signal.
        */}
        {isRollbackLoading && !jobStatus && <RollbackStatusBar />}

        <ChatInput
            onSend={onSendMessage}
            disabled={disabled || isRollbackLoading}
            loading={loading}
            apiConfig={apiConfig}
        />
    </div>
);
